import React, { useEffect, useState } from "react";
import {
  FlatList,
  Image,
  Modal,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { AppColors } from "../../../utility/AppColors";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../utility/NavigationHelper";
import { AppScreens } from "../../Utility/AppScreens";
import { AppImages } from "../../../utility/AppImages";
import Separator from "../../../Components/Separator";
import StreamChatListingComponent from "../../../Components/past-event-components/StreamChatListingComponent";
import EventDetailComponent from "../../../Components/past-event-components/EventDetailComponent";
import PlayerDetail from "../../../Components/past-event-components/PlayerDetail";
import StreamPlayer from "../../../common/StreamPlayer";
import { getLoginData } from "../../../utility/AppStoreData";
import { fetchSpecificPastEvent } from "../../../services/eventsAPI";
import InviteSocialMediaModal from "../Event/InviteSocialMediaModal";
import Header from "./Common/Header";
import { ScreenBGGradientView } from "../../../common/CommonView";
import EventDetailCard from "./OnGoingEvents/EventDetailCard";
import { LinearGradient } from "expo-linear-gradient";
import { AppFontStyle } from "../../../styles/AppFontStyle";
import Avatar from "../../../Components/choose-avatar-components/Avatar";
import EventChatBottomSheet from "./Common/EventChatBottomSheet";
import StarRating from "../../../Components/past-event-components/StarRating";
import PastEventDetailSkeleton from "../../../Components/skeleton-components/PastEventDetailSkeleton";
import Skeletons from "../../../Components/skeleton-components/Skeletons";

const PastEvent = ({ route }) => {
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();
  const styles = useStyles();
  const { eventID } = route?.params || {};
  const eventIdToUse = eventID;

  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isShare, setIsShare] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedButton, setSelectedButton] = useState("Overview");
  const [showChatDrawer, setShowChatDrawer] = useState(false);
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);
  const [selectedStreamer, setSelectedStreamer] = useState({
    name: "Organizer",
    role: "organiser",
  });

  const tabData = [
    { id: 1, title: "Overview" },
    { id: 2, title: "Winner" },
    { id: 3, title: "Stream" },
    { id: 4, title: "Viewers" },
  ];

  const isMobileView =
    deviceSize === DEVICE_SIZES.XS ||
    deviceSize === DEVICE_SIZES.SM ||
    deviceSize === DEVICE_SIZES.MD ||
    deviceSize === DEVICE_SIZES.LG;

  const handleSetShowVideo = (videoDetails) => {
    if (!videoDetails?.streamUrl) {
      console.warn("Invalid video details provided");
      return;
    }
    setSelectedVideo(videoDetails);
  };

  useEffect(() => {
    const fetchEventDetails = async () => {
      setLoading(true);
      try {
        const loginData = await getLoginData();
        if (!loginData) {
          throw new Error("User not logged in");
        }
        const accessToken = loginData.AccessToken;
        const response = await fetchSpecificPastEvent(
          eventIdToUse,
          accessToken
        );

        if (response.statusCode === 200) {
          setEventData(response.body);
        } else {
          throw new Error("Failed to fetch event data");
        }
      } catch (err) {
        console.error("Error fetching event details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (eventIdToUse) {
      fetchEventDetails();
    }
  }, [eventIdToUse]);

  // Update selected streamer name when eventData is loaded
  useEffect(() => {
    if (eventData && selectedStreamer.role === "organiser") {
      setSelectedStreamer({
        name: eventData.event?.organiserId || "Organizer",
        role: "organiser",
      });
    }
  }, [eventData]);

  const renderTabItem = ({ item }) => {
    const isSelected = selectedButton === item.title;
    return (
      <TouchableOpacity
        onPress={() => setSelectedButton(item.title)}
        style={{
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 8,
          borderWidth: 0.8,
          borderColor: isSelected ? "#374151" : "transparent",
          backgroundColor: isSelected
            ? "rgba(31, 41, 55, 0.5)"
            : "transparent",
        }}
      >
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_14,
            { color: AppColors.white, opacity: isSelected ? 1 : 0.6 },
          ]}
        >
          {item.title}
        </Text>
      </TouchableOpacity>
    );
  };

  // Dynamic player data for Winner tab and Desktop VS
  const getPlayerData = (role) => {
    if (!eventData) return null;
    const isOrganizer = role === "organiser";
    const profile = isOrganizer ? eventData.organizerProfile : eventData.challengerProfile;
    const id = isOrganizer ? eventData.event?.organiserId : eventData.event?.challengerId;
    // Use userID instead of firstName for the display name
    const name = id || (isOrganizer ? "Organizer" : "Challenger");
    const label = isOrganizer ? "Kaller" : "Being Kalled out";

    // Check for dispute first
    const isDispute = eventData.dispute === true;

    let badgeText, badgeIcon, borderColor, badgeBg, isWinner;

    if (isDispute) {
      // Dispute case: no winner/runner-up, neutral styling
      badgeText = "";
      badgeIcon = null;
      borderColor = "#374151"; // Neutral gray border
      badgeBg = "#374151";
      isWinner = false;
    } else {
      // Normal case: winner/runner-up logic
      isWinner = eventData.winner === id;
      badgeText = isWinner ? "Winner" : "Runner-up";
      badgeIcon = isWinner ? AppImages.icGoldMedal : AppImages.icSilverMedal;
      borderColor = isWinner ? "#00A63E" : "#4A5565";
      badgeBg = isWinner ? "#00A63E" : "#4A5565";
    }

    return {
      label,
      profile,
      name,
      badgeText,
      badgeIcon,
      borderColor,
      badgeBg,
      isWinner,
      isDispute,
    };
  };

  // Dispute Banner Component
  const DisputeBanner = () => {
    const [timeRemaining, setTimeRemaining] = React.useState("");

    React.useEffect(() => {
      if (!eventData?.event?.eventEndTime) return;

      const updateTimer = () => {
        // Calculate 24 hours from event end time
        const eventEndTime = new Date(eventData.event.eventEndTime).getTime();
        const disputeDeadline = eventEndTime + (24 * 60 * 60 * 1000); // Add 24 hours
        const now = new Date().getTime();
        const diff = disputeDeadline - now;

        if (diff <= 0) {
          setTimeRemaining("00:00:00");
          return;
        }

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        setTimeRemaining(
          `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
        );
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    }, [eventData?.event?.eventEndTime]);

    return (
      <View
        style={{
          width: "100%",
          padding: 16,
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <Text
          style={{
            color: "#DC2626",
            textAlign: "center",
            fontSize: 26,
            fontWeight: "900",
            lineHeight: 30.03,
            width: "100%",
          }}
        >
          Result is in dispute
        </Text>
        <Text
          style={{
            color: "#DC2626",
            textAlign: "center",
            fontSize: 18,
            fontWeight: "500",
            lineHeight: 20.79,
            width: "100%",
          }}
        >
          Please wait for the result as it's gone into dispute
        </Text>
        <Text
          style={{
            color: "#FFF",
            textAlign: "center",
            fontSize: 18,
            fontWeight: "600",
            lineHeight: 20.79,
            width: "100%",
          }}
        >
          Resolving the dispute could take up to 24 hours : {timeRemaining || "00:00:00"}
        </Text>
      </View>
    );
  };

  if (!loading && (!eventData || eventData.event?.status !== "completed")) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#111827' }}>
        <Text style={{ color: AppColors.white }}>Event not completed or not found.</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <ScreenBGGradientView />
      <Header
        btnSearchTapped={() =>
          navigateToScreen(navigation, AppScreens.SearchEventWebScreen)
        }
        btnWalletTapped={() => { }}
        btnStartTutorialTapped={() => { }}
        btnMessageTapped={() => { }}
        btnNotificationTapped={() => { }}
        btnProfileTapped={() => { }}
      />

      <ScrollView
        style={styles.container}
        showsHorizontalScrollIndicator={false}
      >
        {/* Header Section with Back and Title */}
        <View
          style={{
            marginTop: 10,
            flexDirection: "row",
            justifyContent: isMobileView ? "flex-start" : "space-between",
            alignItems: "center",
            gap: 10,
            height: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? "auto" : 40,
            marginBottom: isMobileView ? 10 : 0,
          }}
        >
          {/* Left side: Back button and Title */}
          <View style={{ flexDirection: "row", gap: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 16 : 8, alignItems: "center" }}>
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                width: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 44 : 40,
                height: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 44 : 40,
                padding: 6,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                justifyContent: "center",
                alignItems: "center",
                aspectRatio: 1,
              }}
            >
              <TouchableOpacity
                onPress={() => {
                  navigation.dispatch(StackActions.pop());
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Image
                  source={AppImages.icArrowLeft}
                  style={{ height: 24, width: 24 }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </LinearGradient>
            <Text
              style={{
                color: "#FFFFFF",
                fontSize: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 32 : 16,
                fontWeight: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? "800" : "700",
                lineHeight: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 36.96 : 18.48,
              }}
            >
              Event Result
            </Text>
          </View>

          {/* Right side: Share icon - Desktop only */}
          {!isMobileView && (
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                width: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 47 : 40,
                height: deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 47 : 40,
                padding: 6,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                justifyContent: "center",
                alignItems: "center",
                aspectRatio: 1,
              }}
            >
              <TouchableOpacity
                onPress={() => setIsShare(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Image
                  source={AppImages.Ko_Share}
                  style={{ width: 24, height: 24 }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </LinearGradient>
          )}
        </View>

        {/* Mobile Tabs - Directly after header */}
        {isMobileView && (
          <LinearGradient
            colors={["#1F2937", "#111827"]}
            start={[0, 0]}
            end={[0, 1]}
            style={{
              height: 38,
              paddingVertical: 8,
              paddingHorizontal: 12,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
              marginBottom: 15,
            }}
          >
            <FlatList
              horizontal={true}
              showsHorizontalScrollIndicator={false}
              data={tabData}
              renderItem={renderTabItem}
              keyExtractor={(item) => item.id.toString()}
              contentContainerStyle={{
                flexGrow: 1,
                justifyContent: "space-between",
                alignItems: "center"
              }}
            />
          </LinearGradient>
        )}

        {/* EventDetailCard - Always show for desktop, only on Overview for mobile */}
        {(!isMobileView || (isMobileView && selectedButton === "Overview")) && (
          <View style={{ marginTop: isMobileView ? 0 : undefined }}>
            <EventDetailCard
              setIsMinAttendees={() => { }} // No-op for past events
              details={{
                ...eventData?.event,
                // Add gameImage from root level of response
                gameImage: eventData?.gameImage || eventData?.event?.gameImage,
                // Calculate totalCredits from stakes using sell rates (bronze:5, silver:50, gold:500, platinum:5000)
                totalCredits: (eventData?.event?.stakes || []).reduce((total, stake) => {
                  const rates = { Bronze: 5, Silver: 50, Gold: 500, Platinum: 5000, Coin: 0 };
                  return total + ((rates[stake.type] || 0) * (stake.quantity || 0));
                }, 0),
                // Calculate totalBalance in pounds (same as totalCredits)
                totalBalance: (eventData?.event?.stakes || []).reduce((total, stake) => {
                  const rates = { Bronze: 5, Silver: 50, Gold: 500, Platinum: 5000, Coin: 0 };
                  return total + ((rates[stake.type] || 0) * (stake.quantity || 0));
                }, 0),
              }}
              isMinAttendees={false}
              userData={null}
              timeLeft={(() => {
                // Calculate event duration (endTime - startTime) for past events
                const startTime = eventData?.event?.eventStartTime ? new Date(eventData.event.eventStartTime) : null;
                const endTime = eventData?.event?.eventEndTime ? new Date(eventData.event.eventEndTime) : null;
                if (startTime && endTime) {
                  const duration = endTime.getTime() - startTime.getTime();
                  return {
                    days: Math.floor(duration / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((duration / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((duration / 1000 / 60) % 60),
                    seconds: Math.floor((duration / 1000) % 60),
                  };
                }
                return { days: 0, hours: 0, minutes: 0, seconds: 0 };
              })()}
              setTimeLeft={() => { }} // No-op
              isMoreTimeLeft={false}
              setIsMoreTimeLeft={() => { }} // No-op
              IsEventCanceled={false}
              IsEventCompleted={true}
              isLoading={loading}
              isPastEvent={true}
            />
          </View>
        )}

        {/* Desktop View - Show EventDetailComponent */}
        {!isMobileView && (
          <EventDetailComponent
            styles={styles}
            showLoader={loading}
            eventData={eventData}
            setIsShare={setIsShare}
          />
        )}

        {/* Winner Tab - Mobile */}
        {isMobileView && selectedButton === "Winner" && (
          <View style={{ marginBottom: 20 }}>
            {/* Match Results Title - Only show if not in dispute */}
            {!eventData?.dispute && (
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_18,
                  {
                    color: AppColors.white,
                    textAlign: "center",
                    marginBottom: 20,
                  },
                ]}
              >
                Match Results
              </Text>
            )}

            {/* Player VS Section or Dispute Section */}
            <LinearGradient
              colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                paddingVertical: 15,
                paddingHorizontal: 8,
              }}
            >
              {eventData?.dispute ? (
                // Show Dispute Content
                <DisputeBanner />
              ) : (
                // Show Player VS Section
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {/* Organizer (Kaller) */}
                  <OrganizerPlayerMobile playerData={getPlayerData("organiser")} />

                  {/* VS Icon */}
                  <View style={{ paddingHorizontal: 8 }}>
                    <Image
                      source={AppImages.icVsLogo}
                      style={{ width: 50, height: 120, flexShrink: 0 }}
                      resizeMode="contain"
                    />
                  </View>

                  {/* Challenger (Being Kalled Out) */}
                  <ChallengerPlayerMobile playerData={getPlayerData("challenger")} />
                </View>
              )}
            </LinearGradient>
          </View>
        )}

        {/* Stream Tab - Mobile */}
        {isMobileView && selectedButton === "Stream" && (
          <View>
            {/* Stream Dropdown Selector */}
            <TouchableOpacity
              onPress={() => setShowStreamDropdown(!showStreamDropdown)}
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: 16,
                backgroundColor: "rgba(55, 65, 81, 0.5)",
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                marginBottom: 15,
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_16,
                  { color: AppColors.white },
                ]}
              >
                {selectedStreamer?.name || "Select Streamer"}
              </Text>
              <Image
                source={AppImages.icArrowDown}
                style={{
                  width: 20,
                  height: 20,
                  tintColor: AppColors.white,
                  transform: [{ rotate: showStreamDropdown ? "180deg" : "0deg" }],
                }}
                resizeMode="contain"
              />
            </TouchableOpacity>

            {/* Dropdown Options */}
            {showStreamDropdown && (
              <View
                style={{
                  backgroundColor: "#111827",
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: "#374151",
                  marginBottom: 15,
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.1,
                  shadowRadius: 20,
                  elevation: 10,
                }}
              >
                {/* Organizer Stream Option */}
                <TouchableOpacity
                  onPress={() => {
                    const organizerName = eventData.event?.organiserId || "Organizer";
                    setSelectedStreamer({
                      name: organizerName,
                      role: "organiser",
                    });
                    setShowStreamDropdown(false);
                  }}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 16,
                    gap: 12,
                    borderRadius: 8,
                    backgroundColor: selectedStreamer?.role === "organiser"
                      ? "rgba(220, 38, 38, 0.20)"
                      : "transparent",
                    borderWidth: selectedStreamer?.role === "organiser" ? 0 : 0.3,
                    borderColor: selectedStreamer?.role === "organiser" ? "transparent" : "#8B8B8B",
                    margin: 8,
                  }}
                >
                  <View
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 30,
                      backgroundColor: AppColors.lightPurple_604e74,
                      overflow: "hidden",
                    }}
                  >
                    <Avatar
                      src={eventData.organizerProfile?.profilePicture}
                      variant="profile"
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_16,
                        { color: AppColors.white },
                      ]}
                    >
                      {eventData.event?.organiserId || "Organizer"}
                    </Text>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: "#99A1AF", marginTop: 2 },
                      ]}
                    >
                      Organizer
                    </Text>
                  </View>
                  <View
                    style={{
                      paddingVertical: 6,
                      paddingHorizontal: 12,
                      backgroundColor: getPlayerData("organiser")?.isWinner ? "#00A63E" : "#DC2626",
                      borderRadius: 20,
                    }}
                  >
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_12,
                        { color: AppColors.white },
                      ]}
                    >
                      {getPlayerData("organiser")?.isWinner ? "Winner's Record" : "Watch Record"}
                    </Text>
                  </View>
                </TouchableOpacity>

                {/* Challenger Stream Option */}
                <TouchableOpacity
                  onPress={() => {
                    const challengerName = eventData.event?.challengerId || "Challenger";
                    setSelectedStreamer({
                      name: challengerName,
                      role: "challenger",
                    });
                    setShowStreamDropdown(false);
                  }}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 16,
                    gap: 12,
                    borderRadius: 8,
                    backgroundColor: selectedStreamer?.role === "challenger"
                      ? "rgba(220, 38, 38, 0.20)"
                      : "transparent",
                    borderWidth: selectedStreamer?.role === "challenger" ? 0 : 0.3,
                    borderColor: selectedStreamer?.role === "challenger" ? "transparent" : "#8B8B8B",
                    margin: 8,
                  }}
                >
                  <View
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 30,
                      backgroundColor: AppColors.lightPurple_604e74,
                      overflow: "hidden",
                    }}
                  >
                    <Avatar
                      src={eventData.challengerProfile?.profilePicture}
                      variant="profile"
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_16,
                        { color: AppColors.white },
                      ]}
                    >
                      {eventData.event?.challengerId || "Challenger"}
                    </Text>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: "#99A1AF", marginTop: 2 },
                      ]}
                    >
                      Challenger
                    </Text>
                  </View>
                  <View
                    style={{
                      paddingVertical: 6,
                      paddingHorizontal: 12,
                      backgroundColor: getPlayerData("challenger")?.isWinner ? "#00A63E" : "#7C2D12",
                      borderRadius: 20,
                    }}
                  >
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_12,
                        { color: AppColors.white },
                      ]}
                    >
                      {getPlayerData("challenger")?.isWinner ? "Winner's Record" : "Watch Record"}
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            )}

            {/* Stream Content */}
            {selectedStreamer && (
              <PlayerDetail
                styles={styles}
                setShowVideo={handleSetShowVideo}
                showLoader={loading}
                eventData={eventData}
                userRole={selectedStreamer.role as "organiser" | "challenger"}
              />
            )}
          </View>
        )}

        {/* Viewers Tab - Mobile */}
        {isMobileView && selectedButton === "Viewers" && (
          <View>
            {/* Total Viewers Section */}
            <LinearGradient
              colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                height: 177,
                padding: 16,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                marginBottom: 20,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_18,
                    { color: AppColors.white },
                  ]}
                >
                  Total Viewers
                </Text>
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_24,
                    { color: AppColors.white },
                  ]}
                >
                  {eventData?.event?.attendeeIDs?.length || 0}
                </Text>
              </View>

              {/* Viewer Profiles Grid - Scrollable, max 8 visible */}
              <ScrollView
                horizontal={false}
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                contentContainerStyle={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 16,
                  justifyContent: "flex-start",
                }}
              >
                {eventData?.attendeeProfiles?.slice(0, 8).map((viewer, index) => (
                  <View key={index} style={{ alignItems: "center", width: 60 }}>
                    <View
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: 30,
                        backgroundColor: AppColors.lightPurple_604e74,
                        overflow: "hidden",
                        marginBottom: 4,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        src={viewer.profilePicture}
                        variant="profile"
                        style={{ width: 60, height: 60 }}
                      />
                    </View>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_12,
                        { color: AppColors.white, textAlign: "center" },
                      ]}
                      numberOfLines={1}
                    >
                      {viewer.userId || "User"}
                    </Text>
                  </View>
                ))}
              </ScrollView>
            </LinearGradient>

            {/* Total Gifts and Likes Section */}
            <LinearGradient
              colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                padding: 16,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                marginBottom: 20,
              }}
            >
              {/* Total Gifts and Likes Row */}
              <View style={{ flexDirection: "row", marginBottom: 20 }}>
                {/* Total Gifts in Tokens */}
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      color: "rgba(255, 255, 255, 0.50)",
                      fontSize: 12,
                      fontWeight: "400",
                      marginBottom: 12,
                    }}
                  >
                    Total Gifts in tokens
                  </Text>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <Image
                      source={AppImages.icGiftEvent}
                      style={{ width: 30, height: 30 }}
                      resizeMode="contain"
                    />
                    <Text
                      style={{
                        color: "#FFF",
                        fontSize: 16,
                        fontWeight: "400",
                        lineHeight: 18.48,
                      }}
                    >
                      {eventData?.totalTokens || 0} Tokens
                    </Text>
                  </View>
                </View>

                {/* Total Likes */}
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      color: "rgba(255, 255, 255, 0.50)",
                      fontSize: 12,
                      fontWeight: "400",
                      marginBottom: 12,
                    }}
                  >
                    Total Likes
                  </Text>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <Image
                      source={AppImages.icLikeEvent}
                      style={{ width: 30, height: 30 }}
                      resizeMode="contain"
                    />
                    <Text
                      style={{
                        color: "#FFF",
                        fontSize: 16,
                        fontWeight: "400",
                        lineHeight: 18.48,
                      }}
                    >
                      {eventData?.likeCount || 0}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Feedback Section */}
              <View>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <Text
                    style={{
                      color: "#FFF",
                      fontSize: 14,
                      fontWeight: "700",
                      lineHeight: 16.17,
                    }}
                  >
                    Nice stream! How'd it feel?
                  </Text>
                  <TouchableOpacity
                    onPress={() => {
                      // Handle share action
                      setIsShare(true);
                    }}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      backgroundColor: "rgba(55, 65, 81, 0.5)",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Image
                      source={AppImages.icshare}
                      style={{ width: 24, height: 24, tintColor: AppColors.white }}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>

                {/* Star Rating */}
                <View style={{ marginBottom: 16 }}>
                  <StarRating rating={4} totalStars={5} size={38} />
                </View>

                {/* Give us Feedback Button */}
                <TouchableOpacity
                  style={{
                    width: 152,
                    height: 40,
                    backgroundColor: "#DC2626",
                    borderRadius: 8,
                    paddingVertical: 10,
                    paddingHorizontal: 8,
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 10,
                  }}
                  onPress={() => {
                    // Open WhatsApp link in new tab
                    window.open('https://chat.whatsapp.com/J6ntLXPLDpYAeSmSQ7AgTG?mode=wwt', '_blank');
                  }}
                >
                  <Text
                    style={{
                      color: "#FFF",
                      fontSize: 14,
                      fontWeight: "600",
                      lineHeight: 20,
                    }}
                  >
                    Give us Feedback
                  </Text>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>
        )}

        {/* Desktop View - Full Layout */}
        {!isMobileView && (
          <View
            style={[
              styles.deviceflexDirection,
              {
                justifyContent: "space-between",
                marginBottom: 20,
                gap: 12,
              },
            ]}
          >
            <View
              style={{
                flex: 1,
              }}
            >
              {/* Player VS Section or Dispute Section */}
              <LinearGradient
                colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
                start={[0, 0]}
                end={[1, 1]}
                style={{
                  flexDirection: eventData?.dispute ? "column" : "row",
                  borderRadius: 8,
                  borderWidth: 0.8,
                  borderColor: "#374151",
                  paddingVertical: 10,
                  paddingHorizontal: 10,
                  justifyContent: eventData?.dispute ? "center" : "space-around",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                {eventData?.dispute ? (
                  // Show Dispute Content
                  <DisputeBanner />
                ) : (
                  <>
                    {/* Organizer (Kaller) */}
                    <OrganizerPlayerDesktop playerData={getPlayerData("organiser")} />

                    {/* VS Icon */}
                    <Image
                      source={AppImages.icVsLogo}
                      style={{ width: 80, height: 180.39, flexShrink: 0 }}
                      resizeMode="contain"
                    />

                    {/* Challenger (Being Kalled Out) */}
                    <ChallengerPlayerDesktop playerData={getPlayerData("challenger")} />
                  </>
                )}
              </LinearGradient>

              {selectedVideo ? (
                <View style={{ marginTop: 20, position: "relative" }}>
                  <StreamPlayer
                    platform={selectedVideo?.platform}
                    streamUrl={selectedVideo.streamUrl}
                    height={492}
                    width={"100%"}
                  />
                  <TouchableOpacity
                    onPress={() => setSelectedVideo(null)}
                    style={{ marginTop: 10, position: "absolute", right: 10 }}
                  >
                    <Image
                      source={AppImages.icCloseWhite}
                      style={{ height: 20, width: 20 }}
                    />
                  </TouchableOpacity>
                </View>
              ) : (
                <>
                  <PlayerDetail
                    styles={styles}
                    setShowVideo={handleSetShowVideo}
                    showLoader={loading}
                    eventData={eventData}
                    userRole="organiser"
                  />
                  <Separator
                    color={AppColors.darkGray_595959}
                    height={1}
                    marginTop={20}
                    marginBottom={20}
                    marginHorizontal={0}
                  />
                  <PlayerDetail
                    styles={styles}
                    setShowVideo={handleSetShowVideo}
                    showLoader={loading}
                    eventData={eventData}
                    userRole="challenger"
                  />
                </>
              )}
            </View>

            <StreamChatListingComponent
              styles={styles}
              showLoader={loading}
              eventData={eventData}
            />
          </View>
        )
        }
      </ScrollView >

      {/* Chat Icon for Mobile - Fixed at Bottom Right - Only for Winner, Stream, Viewers tabs */}
      {
        isMobileView && selectedButton !== "Overview" && (
          <TouchableOpacity
            onPress={() => setShowChatDrawer(true)}
            style={{
              position: "absolute",
              bottom: 20,
              right: 20,
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: "#DC2626",
              justifyContent: "center",
              alignItems: "center",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 4,
              elevation: 8,
              zIndex: 1000,
            }}
          >
            <Image
              source={AppImages.icMessageSvg}
              style={{ width: 24, height: 24, tintColor: AppColors.white }}
            />
          </TouchableOpacity>
        )
      }

      {/* Stream Chat Modal for Mobile */}
      {
        isMobileView && showChatDrawer && (
          <Modal
            visible={showChatDrawer}
            transparent={true}
            animationType="slide"
            onRequestClose={() => setShowChatDrawer(false)}
          >
            <View style={{
              flex: 1,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              justifyContent: "flex-end",
            }}>
              <View style={{
                backgroundColor: "#111827",
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                height: "70%",
                paddingBottom: 20,
              }}>
                {/* Header with close button */}
                <View style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: "#374151",
                }}>
                  <Text style={AppFontStyle.BOLD_ANYBODY_18}>Stream Chat</Text>
                  <TouchableOpacity onPress={() => setShowChatDrawer(false)}>
                    <Image
                      source={AppImages.icCloseWhiteSvg}
                      style={{ width: 24, height: 24 }}
                    />
                  </TouchableOpacity>
                </View>
                {/* Stream Chat Content */}
                <ScrollView style={{ maxHeight: 500, padding: 12 }}>
                  {eventData?.chats?.length === 0 || !eventData?.chats ? (
                    <View style={{ padding: 20, alignItems: "center" }}>
                      <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { color: AppColors.gray_A3A3A3 }]}>
                        No chats for this event
                      </Text>
                    </View>
                  ) : (
                    eventData?.chats?.map((item: any, index: number) => (
                      <View
                        key={index}
                        style={{
                          flexDirection: "row",
                          marginVertical: 8,
                          columnGap: 10,
                        }}
                      >
                        <Image
                          source={item.profilePicture ? { uri: item.profilePicture } : AppImages.GiftImg}
                          style={{
                            width: 27,
                            height: 27,
                            borderRadius: 100,
                          }}
                        />
                        <View style={{ flex: 1, flexDirection: "row", columnGap: 5, flexWrap: "wrap" }}>
                          <Text style={AppFontStyle.REGULAR_ANYBODY_14}>
                            {item.userId}:
                          </Text>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_12,
                              { opacity: 0.5, lineHeight: 17 },
                            ]}
                          >
                            {item.commentData}
                          </Text>
                        </View>
                      </View>
                    ))
                  )}
                </ScrollView>
              </View>
            </View>
          </Modal>
        )
      }
    </View >
  );
};

// Helper components for mobile winner tab
const OrganizerPlayerMobile = ({ playerData }) => {
  if (!playerData) {
    return (
      <View style={{ alignItems: "center", gap: 10, flex: 1 }}>
        <Skeletons height={14} width={50} style={{ borderRadius: 4 }} />
        <View style={{
          width: "100%",
          maxWidth: 140,
          height: 140,
          borderRadius: 8,
          backgroundColor: "rgba(31, 41, 55, 0.5)",
          justifyContent: "center",
          alignItems: "center",
          borderWidth: 0.8,
          borderColor: "#374151",
        }}>
          <Skeletons height={60} width={60} style={{ borderRadius: 30 }} />
          <Skeletons height={14} width={80} style={{ borderRadius: 4, marginTop: 10 }} />
        </View>
        <Skeletons height={32} width={100} style={{ borderRadius: 16 }} />
      </View>
    );
  }

  return (
    <View style={{ alignItems: "center", gap: 10, flex: 1 }}>
      <Text
        style={[
          AppFontStyle.BOLD_ANYBODY_12,
          { color: "#DC2626" },
        ]}
      >
        {playerData?.label || "Kaller"}
      </Text>
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
        start={[0, 0]}
        end={[1, 1]}
        style={{
          width: "100%",
          maxWidth: 140,
          minHeight: 140,
          padding: 8,
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: 8,
          borderWidth: 0.8,
          borderColor: playerData?.borderColor || "#4A5565",
          gap: 8,
        }}
      >
        <View
          style={{
            width: 60,
            height: 60,
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 60,
            borderWidth: 2,
            borderColor: "#D2D2D2",
            overflow: "hidden",
          }}
        >
          <Avatar
            src={playerData?.profile?.profilePicture}
            variant="profile"
          />
        </View>
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_12,
            { color: AppColors.white, textAlign: "center" },
          ]}
        >
          @{playerData?.name || "Unknown"}
        </Text>
      </LinearGradient>
      {!playerData?.isDispute && (
        <View
          style={{
            flexDirection: "row",
            paddingVertical: 6,
            paddingHorizontal: 12,
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            borderRadius: 20,
            backgroundColor: playerData?.badgeBg || "#4A5565",
            marginTop: 8,
          }}
        >
          <Image
            source={playerData?.badgeIcon}
            style={{ width: 20, height: 20 }}
            resizeMode="contain"
          />
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white },
            ]}
          >
            {playerData?.badgeText || "Result"}
          </Text>
        </View>
      )}
    </View>
  );
};

const ChallengerPlayerMobile = ({ playerData }) => {
  if (!playerData) {
    return (
      <View style={{ alignItems: "center", gap: 10, flex: 1 }}>
        <Skeletons height={14} width={80} style={{ borderRadius: 4 }} />
        <View style={{
          width: "100%",
          maxWidth: 140,
          height: 140,
          borderRadius: 8,
          backgroundColor: "rgba(31, 41, 55, 0.5)",
          justifyContent: "center",
          alignItems: "center",
          borderWidth: 0.8,
          borderColor: "#374151",
        }}>
          <Skeletons height={60} width={60} style={{ borderRadius: 30 }} />
          <Skeletons height={14} width={80} style={{ borderRadius: 4, marginTop: 10 }} />
        </View>
        <Skeletons height={32} width={100} style={{ borderRadius: 16 }} />
      </View>
    );
  }

  return (
    <View style={{ alignItems: "center", gap: 10, flex: 1 }}>
      <Text
        style={[
          AppFontStyle.BOLD_ANYBODY_12,
          { color: "#DC2626" },
        ]}
      >
        {playerData?.label || "Being Kalled out"}
      </Text>
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
        start={[0, 0]}
        end={[1, 1]}
        style={{
          width: "100%",
          maxWidth: 140,
          minHeight: 140,
          padding: 8,
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: 8,
          borderWidth: 0.8,
          borderColor: playerData?.borderColor || "#4A5565",
          gap: 8,
        }}
      >
        <View
          style={{
            width: 60,
            height: 60,
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 60,
            borderWidth: 2,
            borderColor: "#D2D2D2",
            overflow: "hidden",
          }}
        >
          <Avatar
            src={playerData?.profile?.profilePicture}
            variant="profile"
          />
        </View>
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_12,
            { color: AppColors.white, textAlign: "center" },
          ]}
        >
          @{playerData?.name || "Unknown"}
        </Text>
      </LinearGradient>
      {!playerData?.isDispute && (
        <View
          style={{
            flexDirection: "row",
            paddingVertical: 6,
            paddingHorizontal: 12,
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            borderRadius: 20,
            backgroundColor: playerData?.badgeBg || "#4A5565",
            marginTop: 8,
          }}
        >
          <Image
            source={playerData?.badgeIcon}
            style={{ width: 20, height: 20 }}
            resizeMode="contain"
          />
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white },
            ]}
          >
            {playerData?.badgeText || "Result"}
          </Text>
        </View>
      )}
    </View>
  );
};

// Helper components for desktop VS section
const OrganizerPlayerDesktop = ({ playerData }) => {
  if (!playerData) {
    return (
      <View style={{ alignItems: "center", gap: 12 }}>
        <View style={{
          width: 158,
          height: 153,
          padding: 9,
          borderRadius: 8,
          backgroundColor: "rgba(31, 41, 55, 0.5)",
          justifyContent: "space-between",
          alignItems: "center",
          borderWidth: 0.8,
          borderColor: "#374151",
        }}>
          <Skeletons height={14} width={50} style={{ borderRadius: 4 }} />
          <View style={{ alignItems: "center", gap: 8 }}>
            <Skeletons height={80} width={80} style={{ borderRadius: 40 }} />
            <Skeletons height={14} width={80} style={{ borderRadius: 4 }} />
          </View>
        </View>
        <Skeletons height={32} width={100} style={{ borderRadius: 16 }} />
      </View>
    );
  }

  return (
    <View style={{ alignItems: "center", gap: 12 }}>
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
        start={[0, 0]}
        end={[1, 1]}
        style={{
          width: 158,
          height: 153,
          padding: 9,
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 8,
          borderWidth: 0.8,
          borderColor: playerData?.borderColor || "#4A5565",
        }}
      >
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_14,
            { color: "#DC2626" },
          ]}
        >
          {playerData?.label || "Kaller"}
        </Text>
        <View style={{ alignItems: "center", gap: 8 }}>
          <View
            style={{
              backgroundColor: AppColors.lightPurple_604e74,
              borderRadius: 40,
              overflow: "hidden",
              width: 80,
              height: 80,
              justifyContent: "center",
              alignItems: "center",
              borderWidth: 2,
              borderColor: "#D2D2D2",
            }}
          >
            <Avatar
              src={playerData?.profile?.profilePicture}
              variant="profile"
            />
          </View>
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white, textAlign: "center" },
            ]}
          >
            @{playerData?.name || "Unknown"}
          </Text>
        </View>
      </LinearGradient>
      <View
        style={{
          flexDirection: "row",
          paddingVertical: 6,
          paddingHorizontal: 12,
          justifyContent: "center",
          alignItems: "center",
          gap: 6,
          borderRadius: 20,
          backgroundColor: playerData?.badgeBg || "#4A5565",
        }}
      >
        <Image
          source={playerData?.badgeIcon}
          style={{ width: 20, height: 20 }}
          resizeMode="contain"
        />
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_12,
            { color: AppColors.white },
          ]}
        >
          {playerData?.badgeText || "Result"}
        </Text>
      </View>
    </View>
  );
};

const ChallengerPlayerDesktop = ({ playerData }) => {
  if (!playerData) {
    return (
      <View style={{ alignItems: "center", gap: 12 }}>
        <View style={{
          width: 158,
          height: 153,
          padding: 9,
          borderRadius: 8,
          backgroundColor: "rgba(31, 41, 55, 0.5)",
          justifyContent: "space-between",
          alignItems: "center",
          borderWidth: 0.8,
          borderColor: "#374151",
        }}>
          <Skeletons height={14} width={80} style={{ borderRadius: 4 }} />
          <View style={{ alignItems: "center", gap: 8 }}>
            <Skeletons height={80} width={80} style={{ borderRadius: 40 }} />
            <Skeletons height={14} width={80} style={{ borderRadius: 4 }} />
          </View>
        </View>
        <Skeletons height={32} width={100} style={{ borderRadius: 16 }} />
      </View>
    );
  }

  return (
    <View style={{ alignItems: "center", gap: 12 }}>
      <LinearGradient
        colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
        start={[0, 0]}
        end={[1, 1]}
        style={{
          width: 158,
          height: 153,
          padding: 9,
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 8,
          borderWidth: 0.8,
          borderColor: playerData?.borderColor || "#4A5565",
        }}
      >
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_14,
            { color: "#DC2626" },
          ]}
        >
          {playerData?.label || "Being Kalled out"}
        </Text>
        <View style={{ alignItems: "center", gap: 8 }}>
          <View
            style={{
              backgroundColor: AppColors.lightPurple_604e74,
              borderRadius: 40,
              overflow: "hidden",
              width: 80,
              height: 80,
              justifyContent: "center",
              alignItems: "center",
              borderWidth: 2,
              borderColor: "#D2D2D2",
            }}
          >
            <Avatar
              src={playerData?.profile?.profilePicture}
              variant="profile"
            />
          </View>
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              { color: AppColors.white, textAlign: "center" },
            ]}
          >
            @{playerData?.name || "Unknown"}
          </Text>
        </View>
      </LinearGradient>
      <View
        style={{
          flexDirection: "row",
          paddingVertical: 6,
          paddingHorizontal: 12,
          justifyContent: "center",
          alignItems: "center",
          gap: 6,
          borderRadius: 20,
          backgroundColor: playerData?.badgeBg || "#4A5565",
        }}
      >
        <Image
          source={playerData?.badgeIcon}
          style={{ width: 20, height: 20 }}
          resizeMode="contain"
        />
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_12,
            { color: AppColors.white },
          ]}
        >
          {playerData?.badgeText || "Result"}
        </Text>
      </View>
    </View>
  );
};

export default PastEvent;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      backgroundColor: "transparent",
      flex: 1,
      paddingHorizontal: "2.5%",
      paddingTop: "0.5%",
    },
    gameshowOuterView: {
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 15,
    },
    deviceflexDirection: {
      flexDirection: "row",
    },
    eventDetailsContainer: {
      alignSelf: "flex-end",
      marginBottom: 10,
      paddingVertical: 20,
    },
    countdownContainer: {
      backgroundColor: "transparent",
      borderRadius: 20,
      padding: 20,
    },
    streamChatView: {
      width: "19%",
    },
    progressView: {
      width: "100%",
    },
    secondLineCircle: {
      flexDirection: "row",
      justifyContent: "center",
      marginTop: 30,
      columnGap: 80,
    },
    modalOverlay: {
      flex: 1,
      justifyContent: "flex-start",
      alignItems: "flex-start",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
    popoverContainer: {
      position: "absolute",
      width: 250,
      backgroundColor: "#333",
      borderRadius: 15,
      paddingLeft: 20,
      paddingRight: 20,
      paddingTop: 10,
      paddingBottom: 20,
    },
    closeButton: {
      paddingBottom: 10,
      alignSelf: "flex-end",
    },
    dropdownItem: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingRight: 10,
      paddingVertical: 10,
      borderBottomColor: "#444",
      borderBottomWidth: 1,
    },
    dropdownText: {
      color: "white",
      fontSize: 16,
    },
    dropdownValue: {
      color: "white",
      fontSize: 16,
    },
    searchInput: {
      backgroundColor: "#444",
      borderRadius: 10,
      padding: 10,
      marginBottom: 10,
      color: "white",
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      deviceflexDirection: {
        flexDirection: "column",
      },
      eventDetailsContainer: {
        alignSelf: "center",
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
    },
    [DEVICE_SIZES.MD]: {
      deviceflexDirection: {
        flexDirection: "column",
      },
      eventDetailsContainer: {
        alignSelf: "center",
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
    },
    [DEVICE_SIZES.SM]: {
      deviceflexDirection: {
        flexDirection: "column",
      },
      eventDetailsContainer: {
        borderRadius: 10,
        alignSelf: "flex-end",
      },
      streamChatView: {
        marginHorizontal: 10,
        width: "97%",
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      secondLineCircle: {
        columnGap: 45,
      },
    },
    [DEVICE_SIZES.XS]: {
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      eventDetailsContainer: {
        alignSelf: "center",
        flex: 1,
        marginHorizontal: 15,
      },
      streamChatView: {
        marginHorizontal: 10,
        width: "95%",
      },
      secondLineCircle: {
        columnGap: 45,
      },
    },
  }
);